import React, { useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, IconButton, Stack, TextField, TextFieldProps } from "@mui/material";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { debounce } from "lodash";

import { Characteristic, CharacteristicData } from "..";
import { CharAutocomplete } from "../CharAutocomplete";

const currencyCharVarName = "Currency";
const primaryCurrency = "Primary Currency";

interface Props {
  characteristic: Characteristic;
  characteristicData: CharacteristicData;
  filterCurrenciesOptions: () => string[];
  onRemove?: () => void;
  onUpdate: (params: Partial<Pick<CharacteristicData, "value" | "percentage">>) => void;
}

const Inputs: Record<string, React.FC<TextFieldProps>> = {
  text: TextField,
  number: NumberInput,
  percentage: NumberInput,
  currency: NumberInput
};

export const DataItem: React.FC<Props> = ({
  characteristic,
  characteristicData,
  filterCurrenciesOptions,
  onRemove,
  onUpdate
}) => {
  const { data_type, name } = characteristic;
  const { value, percentage } = characteristicData;

  const [localValue, setLocalValue] = useState<string>(value);
  const [localPercentage, setLocalPercentage] = useState<number>(percentage);

  const onUpdateDebounce = useMemo(() => debounce(onUpdate, 500), [onUpdate]);

  const handleUpdateValue = (value: string) => {
    setLocalValue(value);
    onUpdateDebounce({ value });
  };

  const handleUpdatePercentage = (percentage: number) => {
    setLocalPercentage(percentage);
    onUpdateDebounce({ percentage });
  };

  useEffect(() => {
    if (value === localValue) return;
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (percentage === localPercentage) return;
    setLocalPercentage(percentage);
  }, [percentage]);

  const Input = name !== currencyCharVarName ? Inputs[data_type] : null;

  return (
    <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "16px" }}>
      {/* {Input && (
        <Input
          size="small"
          fullWidth
          value={localValue}
          onChange={(e) => handleUpdateValue(e.target.value)}
          {...(data_type === "percentage" && { numberInputProps: { suffix: "%", decimalScale: 2 } })}
        />
      )} */}
      {Input && (
        <CharAutocomplete
          defaultValue={localValue}
          onChange={handleUpdateValue}
          characteristicId={characteristic.id as number}
          charName=""
          disabled={false}
        />
      )}
      {(data_type === "currency" && name === currencyCharVarName) ||
        (name === primaryCurrency && (
          <Autocomplete
            options={filterCurrenciesOptions()}
            value={localValue}
            fullWidth
            onChange={(_, option) => handleUpdateValue(option)}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Currency" />}
          />
        ))}
      <NumberInput
        size="small"
        label="Share"
        sx={{ width: 120 }}
        value={localPercentage}
        onChange={(e) => handleUpdatePercentage(Number(e.target.value))}
        numberInputProps={{
          suffix: "%",
          allowNegative: false,
          isNumericString: true,
          decimalScale: 2
        }}
      />
      {onRemove ? (
        <IconButton size="sm" sx={{ bgcolor: "#f5f6f7" }} onClick={onRemove}>
          <CloseIcon />
        </IconButton>
      ) : (
        <Box sx={{ width: 24, flexShrink: 0 }} />
      )}
    </Stack>
  );
};
